import * as React from "react";

import { useNextDevServer } from "./context";
import { normalizeRoute } from "./utils";

const MAX_RENDERS_BEFORE_ERROR = 15;

export function usePage(pageRoute: string) {
  const initialRenderAtRef = React.useRef(Date.now());
  const [rerenderCount, setForceRerender] = React.useState(0);

  const { url, pageStatusMap, refreshEpoch } = useNextDevServer();

  const normalizedRoute = React.useMemo(
    () => normalizeRoute(pageRoute),
    [pageRoute],
  );

  const pageUrl = React.useMemo(() => {
    // NOTE (Gabe 2025-08-13): If the page has an import error then we don't
    // want to render it because it will break the dev server.
    if (pageStatusMap[normalizedRoute]?.hasImportError) {
      return undefined;
    }
    return url && normalizedRoute !== undefined
      ? `${url}/${encodeURI(normalizedRoute)}`
      : undefined;
  }, [normalizedRoute, url, pageStatusMap]);

  const hasImportError =
    pageStatusMap[normalizedRoute]?.hasImportError === true;

  const isLoading =
    !hasImportError &&
    (!pageStatusMap[normalizedRoute] ||
      pageStatusMap[normalizedRoute].timestamp < initialRenderAtRef.current ||
      (pageStatusMap[normalizedRoute]?.status === 404 &&
        rerenderCount < MAX_RENDERS_BEFORE_ERROR));

  const isError = !isLoading && pageStatusMap[normalizedRoute]?.status !== 200;

  React.useEffect(() => {
    if (
      pageStatusMap[normalizedRoute]?.status === 404 &&
      pageStatusMap[normalizedRoute]?.timestamp > initialRenderAtRef.current
    ) {
      const timeout = setTimeout(() => {
        setForceRerender((prev) =>
          prev < MAX_RENDERS_BEFORE_ERROR ? prev + 1 : prev,
        );
      }, 400);

      return () => clearTimeout(timeout);
    }
  }, [pageStatusMap, normalizedRoute]);

  return {
    pageUrl,
    isLoading,
    isError,
    iframeKey: rerenderCount + refreshEpoch,
  } as const;
}
